<!--
  TODO: switch to new firestoreService

  This is the main view for the purchase wizard.
  It is used to create a new purchase transaction.
  It is a wizard with 5 steps:
  1. Person
  2. Vehicle
  3. Items
  4. Payment
  5. Review
-->
<template>
  <div class="transaction-page">
    <div
      v-if="transactionStore.loading || isInitializing"
      class="flex flex-column align-items-center py-5"
    >
      <i class="pi pi-spin pi-spinner text-6xl text-primary mb-4"></i>
      <p>{{ isInitializing ? 'Initializing wizard...' : 'Loading transaction data...' }}</p>
    </div>

    <div v-else>
      <div class="flex align-items-center justify-content-between mb-4">
        <div class="flex align-items-center gap-4">
          <h2 class="text-2xl font-semibold m-0">Purchase Ticket</h2>

          <!-- Person/Organization Display -->
          <div
            v-if="selectedPersonId || selectedOrganizationId"
            class="flex align-items-center gap-3"
          >
            <div class="flex align-items-center gap-2">
              <i class="pi pi-user"></i>
              <span class="font-semibold">
                {{ selectedPerson?.firstName }} {{ selectedPerson?.lastName }}
                <span v-if="showSupplierQuestionnaireStep" class="ml-2">
                  <Badge value="New" severity="success" class="text-xs"></Badge>
                </span>
              </span>
            </div>
            <div v-if="selectedOrganization" class="flex align-items-center gap-2">
              <i class="pi pi-building"></i>
              <span>{{ selectedOrganization.name }}</span>
            </div>
          </div>
        </div>

        <div class="flex gap-2">
          <Button
            v-if="isDev"
            label="Scrub"
            icon="pi pi-trash"
            class="p-button-outlined"
            @click="scrub"
          />
          <Button label="Cancel" icon="pi pi-times" class="p-button-outlined" @click="cancel" />
        </div>
      </div>

      <div class="card">
        <Stepper v-model:value="activeStep">
          <StepList>
            <Step v-slot="{ activateCallback, value, a11yAttrs }" asChild :value="1">
              <div class="flex flex-row flex-auto gap-2 items-center" v-bind="a11yAttrs.root">
                <Button
                  class="p-0 w-3rem h-3rem button-secondary bg-transparent border-0 flex-shrink-0"
                  @click="activateCallback"
                  v-bind="a11yAttrs.header"
                  severity="secondary"
                  :title="
                    selectedPersonId && !satisfyPersonRules()
                      ? getPersonValidationErrors().join(', ')
                      : ''
                  "
                >
                  <span
                    :class="[
                      'border-circle border-2 inline-flex align-items-center justify-content-center w-full h-full',
                      step1Complete
                        ? 'bg-primary text-primary-contrast border-primary'
                        : selectedPersonId && !satisfyPersonRules()
                          ? 'bg-orange-500 text-white border-orange-500'
                          : Number(value) <= activeStep
                            ? 'bg-gray-500 text-white border-gray-500'
                            : 'bg-white text-gray-600 border-gray-500',
                    ]"
                  >
                    <i class="pi pi-id-card"></i>
                  </span>
                </Button>
                <Divider />
              </div>
            </Step>
            <Step v-slot="{ activateCallback, value, a11yAttrs }" asChild :value="2">
              <div class="flex flex-row flex-auto gap-2 pl-2 items-center" v-bind="a11yAttrs.root">
                <Button
                  class="p-0 w-3rem h-3rem bg-transparent border-0 flex-shrink-0"
                  @click="activateCallback"
                  v-bind="a11yAttrs.header"
                >
                  <span
                    :class="[
                      'border-circle border-2 inline-flex align-items-center justify-content-center w-full h-full',
                      step2Complete
                        ? 'bg-primary text-primary-contrast border-primary'
                        : transaction.vehicleSnapshot && !satisfyVehicleRules()
                          ? 'bg-orange-500 text-white border-orange-500'
                          : Number(value) <= activeStep
                            ? 'bg-gray-500 text-white border-gray-500'
                            : 'bg-white text-gray-600 border-gray-500',
                    ]"
                  >
                    <i class="pi pi-car"></i>
                  </span>
                </Button>
                <Divider />
              </div>
            </Step>
            <Step v-slot="{ activateCallback, value, a11yAttrs }" asChild :value="3">
              <div class="flex flex-row flex-auto gap-2 pl-2 items-center" v-bind="a11yAttrs.root">
                <Button
                  class="p-0 w-3rem h-3rem bg-transparent border-0 flex-shrink-0"
                  @click="activateCallback"
                  v-bind="a11yAttrs.header"
                >
                  <span
                    :class="[
                      'border-circle border-2 inline-flex align-items-center justify-content-center w-full h-full',
                      step3Complete
                        ? 'bg-primary text-primary-contrast border-primary'
                        : (transaction.items || []).length > 0 && !step3Complete
                          ? 'bg-orange-500 text-white border-orange-500'
                          : Number(value) <= activeStep
                            ? 'bg-gray-500 text-white border-gray-500'
                            : 'bg-white text-gray-600 border-gray-500',
                    ]"
                  >
                    <i class="pi pi-shopping-cart"></i>
                  </span>
                </Button>
                <Divider />
              </div>
            </Step>
            <Step v-slot="{ activateCallback, value, a11yAttrs }" asChild :value="4">
              <div class="flex flex-row flex-auto gap-2 pl-2 items-center" v-bind="a11yAttrs.root">
                <Button
                  class="p-0 w-3rem h-3rem bg-transparent border-0 flex-shrink-0"
                  @click="activateCallback"
                  v-bind="a11yAttrs.header"
                >
                  <span
                    :class="[
                      'border-circle border-2 inline-flex align-items-center justify-content-center w-full h-full',
                      step4Complete
                        ? 'bg-primary text-primary-contrast border-primary'
                        : Number(value) <= activeStep
                          ? 'bg-gray-500 text-white border-gray-500'
                          : 'bg-white text-gray-600 border-gray-500',
                    ]"
                  >
                    <i class="pi pi-user-edit"></i>
                  </span>
                </Button>
                <Divider />
              </div>
            </Step>
            <Step v-slot="{ activateCallback, value, a11yAttrs }" asChild :value="5">
              <div class="flex flex-row pl-2 items-center" v-bind="a11yAttrs.root">
                <Button
                  class="p-0 w-3rem h-3rem bg-transparent border-0 flex-shrink-0"
                  @click="activateCallback"
                  v-bind="a11yAttrs.header"
                >
                  <span
                    :class="[
                      'border-circle border-2 inline-flex align-items-center justify-content-center w-full h-full',
                      step5Ready
                        ? 'bg-primary text-primary-contrast border-primary'
                        : Number(value) <= activeStep
                          ? 'bg-gray-500 text-white border-gray-500'
                          : 'bg-white text-gray-600 border-gray-500',
                    ]"
                  >
                    <i class="pi pi-eye"></i>
                  </span>
                </Button>
              </div>
            </Step>
          </StepList>

          <StepPanels>
            <!-- Step 1: Party Info -->
            <StepPanel :value="1">
              <div class="grid">
                <div class="col-12">
                  <Panel class="mb-4 person-panel" header="Person" style="height: 100%">
                    <template #header>
                      <div class="flex align-items-center">
                        <i class="pi pi-user mr-2"></i>
                        <span>Person</span>
                      </div>
                      <div v-if="selectedPersonId" class="ml-auto flex gap-2">
                        <Button
                          label="Edit"
                          icon="pi pi-pencil"
                          class="p-button-sm p-button-outlined"
                          @click="editSelectedPerson"
                        />
                        <Button
                          label="Clear"
                          icon="pi pi-times"
                          class="p-button-sm p-button-text p-button-danger"
                          @click="clearSelectedPerson"
                        />
                      </div>
                    </template>
                    <!-- Initial split view when no person is selected or form is not active -->
                    <div
                      v-if="!selectedPersonId && !showPersonForm && !showManualSearch"
                      class="h-full"
                    >
                      <div class="grid h-full">
                        <!-- Left side - Scan ID -->
                        <div
                          class="col-12 md:col-6 flex flex-column align-items-center justify-content-center p-4 border-right-0 md:border-right-1 surface-border"
                        >
                          <div class="text-center">
                            <i class="pi pi-qrcode text-6xl text-primary mb-4"></i>
                            <h3 class="text-xl font-semibold mb-3">Scan ID</h3>
                            <p class="text-gray-600 mb-4">
                              Use the ID scanner to quickly find or create a contact
                            </p>
                            <Button
                              label="Scan ID"
                              icon="pi pi-qrcode"
                              class="p-button-lg"
                              @click="openIdScanner"
                            />
                          </div>
                        </div>

                        <!-- Mobile separator -->
                        <div class="w-full my-4 border-top-1 surface-border md:hidden"></div>
                        <!-- Right side - Manual Search -->
                        <div
                          class="col-12 md:col-6 flex flex-column align-items-center justify-content-center p-4"
                        >
                          <div class="text-center">
                            <i class="pi pi-search text-6xl text-primary mb-4"></i>
                            <h3 class="text-xl font-semibold mb-3">Manual Search</h3>
                            <p class="text-gray-600 mb-4">
                              Search for existing contacts or create a new one
                            </p>
                            <Button
                              label="Search"
                              icon="pi pi-search"
                              class="p-button-lg p-button-outlined"
                              @click="startFullSearch"
                            />
                          </div>
                        </div>
                      </div>
                    </div>

                    <!-- Manual search interface -->
                    <div v-else-if="showManualSearch && !selectedPersonId" class="h-full">
                      <div class="grid h-full">
                        <!-- Left side - Scan ID (kept visible when not in full search) -->
                        <div
                          v-if="!showFullSearch"
                          class="col-12 md:col-6 flex flex-column align-items-center justify-content-center p-4 border-right-0 md:border-right-1 surface-border"
                        >
                          <div class="text-center">
                            <i class="pi pi-qrcode text-6xl text-primary mb-4"></i>
                            <h3 class="text-xl font-semibold mb-3">Scan ID</h3>
                            <p class="text-gray-600 mb-4">
                              Use the ID scanner to quickly find or create a contact
                            </p>
                            <Button
                              label="Scan ID"
                              icon="pi pi-qrcode"
                              class="p-button-lg"
                              @click="openIdScanner"
                            />
                          </div>
                        </div>

                        <!-- Search fields - takes full width when in full search mode -->
                        <div
                          :class="showFullSearch ? 'col-12' : 'col-12 md:col-6'"
                          class="flex flex-column p-4 surface-border"
                        >
                          <div class="flex align-items-center justify-content-between mb-4">
                            <h4 class="m-0">Search for Contact</h4>
                            <Button
                              icon="pi pi-times"
                              class="p-button-rounded p-button-text"
                              @click="closeSearch"
                            />
                          </div>

                          <div class="flex-1">
                            <div class="grid">
                              <div class="col-12 mb-3">
                                <span class="p-float-label">
                                  <InputText
                                    id="searchFirstName"
                                    v-model="searchFirstName"
                                    class="w-full"
                                    placeholder="Enter first name"
                                  />
                                  <label for="searchFirstName">First Name</label>
                                </span>
                              </div>

                              <div class="col-12 mb-3">
                                <span class="p-float-label">
                                  <InputText
                                    id="searchLastName"
                                    v-model="searchLastName"
                                    class="w-full"
                                    placeholder="Enter last name"
                                  />
                                  <label for="searchLastName">Last Name</label>
                                </span>
                              </div>
                            </div>

                            <div class="flex justify-content-center mb-4">
                              <Button
                                label="Search"
                                icon="pi pi-search"
                                class="p-button-lg"
                                :disabled="!searchFirstName && !searchLastName"
                                @click="performManualSearch"
                              />
                            </div>

                            <!-- Search results -->
                            <div v-if="manualSearchResults.length > 0" class="mb-4">
                              <h5 class="mb-3">Search Results</h5>
                              <div class="grid">
                                <div
                                  v-for="person in displayedSearchResults"
                                  :key="person.id"
                                  class="col-12 mb-2"
                                >
                                  <div
                                    class="p-3 border-1 surface-border border-round cursor-pointer hover:surface-100"
                                    @click="onPersonSelect({ value: person })"
                                  >
                                    <div class="flex align-items-center justify-content-between">
                                      <div class="flex-1">
                                        <div class="flex align-items-center gap-2 mb-1">
                                          <div class="font-semibold">
                                            {{ person.firstName }} {{ person.lastName }}
                                          </div>
                                          <Badge
                                            :value="person.status || 'unknown'"
                                            :severity="getStatusSeverity(person.status)"
                                            class="text-xs"
                                          />
                                        </div>
                                        <div v-if="person.email" class="text-sm text-gray-600 mb-1">
                                          <i class="pi pi-envelope mr-1"></i>{{ person.email }}
                                        </div>
                                        <div v-if="person.phone" class="text-sm text-gray-600 mb-1">
                                          <i class="pi pi-phone mr-1"></i>{{ person.phone }}
                                        </div>
                                        <div
                                          class="flex align-items-center gap-3 text-xs text-gray-500"
                                        >
                                          <div
                                            v-if="person.lastTransactionDate"
                                            class="flex align-items-center gap-1"
                                          >
                                            <i class="pi pi-calendar"></i>
                                            <span
                                              >Last visit:
                                              {{ formatDate(person.lastTransactionDate) }}</span
                                            >
                                          </div>
                                          <div class="flex align-items-center gap-1">
                                            <i class="pi pi-shopping-cart"></i>
                                            <span
                                              >{{
                                                transactionStore.loading
                                                  ? '...'
                                                  : person.id
                                                    ? getTransactionCount(person.id)
                                                    : 0
                                              }}
                                              transactions</span
                                            >
                                          </div>
                                        </div>
                                      </div>
                                      <i class="pi pi-arrow-right text-gray-400"></i>
                                    </div>
                                  </div>
                                </div>
                              </div>

                              <!-- More results button -->
                              <div
                                v-if="!showAllSearchResults && nonActiveSearchResults.length > 0"
                                class="text-center mt-3"
                              >
                                <Button
                                  label="More results"
                                  text
                                  class="p-button-text"
                                  @click="toggleShowAllResults"
                                />
                                <div class="text-xs text-gray-500 mt-1">
                                  {{ nonActiveSearchResults.length }} inactive/lead contacts
                                </div>
                              </div>

                              <!-- Show less button -->
                              <div
                                v-if="showAllSearchResults && nonActiveSearchResults.length > 0"
                                class="text-center mt-3"
                              >
                                <Button
                                  label="Show less"
                                  text
                                  class="p-button-text"
                                  @click="toggleShowAllResults"
                                />
                              </div>
                            </div>

                            <!-- No results message -->
                            <div
                              v-else-if="manualSearchPerformed && manualSearchResults.length === 0"
                              class="text-center mb-4"
                            >
                              <p class="text-gray-600 mb-4">
                                No contacts found matching your search criteria.
                              </p>
                              <Button
                                label="Create New Contact"
                                icon="pi pi-plus"
                                class="p-button-outlined"
                                @click="startCreatePerson"
                              />
                            </div>

                            <!-- Initial state -->
                            <div v-else class="text-center">
                              <p class="text-gray-600 mb-4">
                                Enter at least a first name or last name to search
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <!-- Person form (for creating new person or editing) -->
                    <div v-else-if="showPersonForm" class="h-full">
                      <div class="flex flex-column h-full">
                        <div class="flex align-items-center justify-content-between mb-4">
                          <h4 class="m-0">
                            {{ isEditingPerson ? 'Edit Contact' : 'Create New Contact' }}
                          </h4>
                          <Button
                            icon="pi pi-times"
                            class="p-button-rounded p-button-text"
                            @click="cancelPersonForm"
                          />
                        </div>

                        <div class="flex-1 overflow-y-auto">
                          <div class="grid">
                            <!-- Basic Info Fields -->
                            <div class="col-12 md:col-6 mb-3">
                              <span class="p-float-label">
                                <InputText
                                  id="firstName"
                                  v-model="personForm.firstName"
                                  class="w-full"
                                />
                                <label for="firstName">First Name*</label>
                              </span>
                              <small
                                v-if="personFormSubmitted && !personForm.firstName"
                                class="p-error"
                              >
                                First name is required.
                              </small>
                            </div>

                            <div class="col-12 md:col-6 mb-3">
                              <span class="p-float-label">
                                <InputText
                                  id="lastName"
                                  v-model="personForm.lastName"
                                  class="w-full"
                                />
                                <label for="lastName">Last Name*</label>
                              </span>
                              <small
                                v-if="personFormSubmitted && !personForm.lastName"
                                class="p-error"
                              >
                                Last name is required.
                              </small>
                            </div>

                            <div class="col-12 md:col-6 mb-3">
                              <span class="p-float-label">
                                <InputText
                                  id="middleName"
                                  v-model="personForm.middleName"
                                  class="w-full"
                                />
                                <label for="middleName">Middle Name</label>
                              </span>
                            </div>

                            <div class="col-12 md:col-6 mb-3">
                              <span class="p-float-label">
                                <InputText
                                  id="idNumber"
                                  v-model="personForm.idNumber"
                                  class="w-full"
                                />
                                <label for="idNumber">ID #</label>
                              </span>
                            </div>

                            <div class="col-12 md:col-6 mb-3">
                              <span class="p-float-label">
                                <InputText id="email" v-model="personForm.email" class="w-full" />
                                <label for="email">Email</label>
                              </span>
                            </div>

                            <div class="col-12 md:col-6 mb-3">
                              <span class="p-float-label">
                                <InputMask
                                  id="phone"
                                  v-model="personForm.phone"
                                  mask="(999) 999-9999"
                                  class="w-full"
                                />
                                <label for="phone">Phone</label>
                              </span>
                            </div>

                            <div class="col-12 mb-3">
                              <span class="p-float-label">
                                <InputText
                                  id="streetAddress"
                                  v-model="personForm.streetAddress"
                                  class="w-full"
                                />
                                <label for="streetAddress">Street Address</label>
                              </span>
                            </div>

                            <div class="col-12 md:col-6 mb-3">
                              <span class="p-float-label">
                                <InputText id="city" v-model="personForm.city" class="w-full" />
                                <label for="city">City</label>
                              </span>
                            </div>

                            <div class="col-12 md:col-6 mb-3">
                              <span class="p-float-label">
                                <InputText id="state" v-model="personForm.state" class="w-full" />
                                <label for="state">State/Province</label>
                              </span>
                            </div>

                            <div class="col-12 md:col-6 mb-3">
                              <span class="p-float-label">
                                <InputText
                                  id="postalCode"
                                  v-model="personForm.postalCode"
                                  class="w-full"
                                />
                                <label for="postalCode">Postal Code</label>
                              </span>
                            </div>

                            <div class="col-12 md:col-6 mb-3">
                              <span class="p-float-label">
                                <InputText
                                  id="country"
                                  v-model="personForm.country"
                                  class="w-full"
                                />
                                <label for="country">Country</label>
                              </span>
                            </div>
                          </div>
                        </div>

                        <div
                          class="flex justify-content-end gap-2 pt-3 border-top-1 surface-border"
                        >
                          <Button
                            label="Cancel"
                            icon="pi pi-times"
                            class="p-button-text"
                            @click="cancelPersonForm"
                          />
                          <Button
                            :label="isEditingPerson ? 'Update' : 'Save'"
                            icon="pi pi-check"
                            @click="savePersonForm"
                            :loading="savingPerson"
                          />
                        </div>
                      </div>
                    </div>

                    <!-- Selected person display -->
                    <div v-else-if="selectedPersonId" class="h-full">
                      <div class="flex flex-column h-full">
                        <!-- Validation Status -->
                        <div
                          v-if="!satisfyPersonRules()"
                          class="mb-3 p-3 border-1 border-orange-200 surface-orange-50 border-round"
                        >
                          <div class="flex align-items-center gap-2 mb-2">
                            <i class="pi pi-exclamation-triangle text-orange-600"></i>
                            <span class="font-semibold text-orange-700"
                              >Missing Required Information</span
                            >
                          </div>
                          <div class="text-sm text-orange-600">
                            <ul class="m-0 pl-3">
                              <li v-for="error in getPersonValidationErrors()" :key="error">
                                {{ error }}
                              </li>
                            </ul>
                          </div>
                        </div>

                        <div class="flex-1 surface-border border-round">
                          <div class="flex flex-column md:flex-row gap-4">
                            <!-- ID Image Preview -->
                            <div
                              v-if="transactionModel?.party.idInfo.image"
                              class="flex-shrink-0 flex justify-content-center md:justify-content-start"
                            >
                              <img
                                :src="transactionModel.party.idInfo.image"
                                alt="ID Photo"
                                class="border-round shadow-1"
                                style="height: 10rem; object-fit: cover"
                              />
                            </div>
                            <div
                              v-else
                              class="flex-shrink-0 flex justify-content-center md:justify-content-start"
                            >
                              <div
                                class="border-1 border-dashed surface-border border-round flex align-items-center justify-content-center cursor-pointer hover:surface-100 transition-colors"
                                style="height: 10rem; width: 8rem"
                                @click="openIdCaptureDialog"
                              >
                                <div class="text-center text-gray-500">
                                  <i class="pi pi-image text-3xl mb-2"></i>
                                  <div class="text-sm">No ID Photo</div>
                                  <span class="text-xs">Click to upload</span>
                                </div>
                              </div>
                            </div>

                            <!-- Person Details -->
                            <div class="flex-1 flex flex-column gap-3">
                              <div class="text-xl font-semibold">
                                {{ transactionModel?.party.name || 'Unknown' }}
                              </div>
                              <div
                                v-if="selectedPerson?.email || transaction.personSnapshot?.email"
                                class="flex align-items-center gap-2"
                              >
                                <i class="pi pi-envelope text-sm"></i>
                                <span>{{
                                  selectedPerson?.email || transaction.personSnapshot?.email
                                }}</span>
                              </div>
                              <!-- Phone number display or inline form -->
                              <div
                                v-if="selectedPerson?.phone || transaction.personSnapshot?.phone"
                                class="flex align-items-center gap-2"
                              >
                                <i class="pi pi-phone text-sm"></i>
                                <span>{{
                                  selectedPerson?.phone || transaction.personSnapshot?.phone
                                }}</span>
                              </div>
                              <!-- Inline phone form when no phone number exists -->
                              <div
                                v-else-if="selectedPerson && !isEditingPhone"
                                class="flex align-items-center gap-2"
                              >
                                <i class="pi pi-phone text-sm text-gray-400"></i>
                                <Button
                                  label="Add phone number"
                                  icon="pi pi-plus"
                                  size="small"
                                  text
                                  severity="secondary"
                                  @click="startEditingPhone"
                                />
                              </div>
                              <!-- Inline phone editing form -->
                              <div v-else-if="isEditingPhone" class="flex align-items-center gap-2">
                                <i class="pi pi-phone text-sm"></i>
                                <div class="flex align-items-center gap-2 flex-1">
                                  <InputMask
                                    v-model="inlinePhoneValue"
                                    mask="(999) 999-9999"
                                    placeholder="Phone number"
                                    class="flex-1"
                                    size="small"
                                    @keyup.enter="savePhoneNumber"
                                    @keyup.escape="cancelEditingPhone"
                                  />
                                  <Button
                                    icon="pi pi-check"
                                    size="small"
                                    severity="success"
                                    :loading="savingPhone"
                                    @click="savePhoneNumber"
                                  />
                                  <Button
                                    icon="pi pi-times"
                                    size="small"
                                    severity="secondary"
                                    @click="cancelEditingPhone"
                                  />
                                </div>
                              </div>
                              <div
                                v-if="transactionModel?.party.address"
                                class="flex align-items-center gap-2"
                              >
                                <i class="pi pi-map-marker text-sm"></i>
                                <span>{{ transactionModel?.party.address }}</span>
                              </div>
                              <div
                                v-if="
                                  selectedPerson?.idNumber || transaction.personSnapshot?.idNumber
                                "
                                class="flex align-items-center gap-2"
                              >
                                <i class="pi pi-id-card text-sm"></i>
                                <span
                                  >ID:
                                  {{
                                    selectedPerson?.idNumber || transaction.personSnapshot?.idNumber
                                  }}</span
                                >
                              </div>
                            </div>
                          </div>
                        </div>

                        <!-- Other Documents section -->
                        <div class="mt-4">
                          <h3
                            class="text-lg font-medium mb-2 cursor-pointer hover:text-primary transition-colors"
                            @click="toggleFileUpload"
                          >
                            Add Other Documents or IDs
                            <i
                              :class="showFileUpload ? 'pi pi-chevron-up' : 'pi pi-chevron-down'"
                              class="ml-2"
                            ></i>
                          </h3>
                          <!-- File Upload -->
                          <div v-if="showFileUpload" class="mb-3">
                            <div
                              class="flex align-items-center justify-content-center flex-column p-4 cursor-pointer hover:surface-100 transition-colors border-2 border-dashed border-round"
                              @click="triggerFileSelect"
                            >
                              <i class="pi pi-cloud-upload text-4xl text-500 mb-3"></i>
                              <p class="text-500 mb-0">Click to add other IDs or documents</p>
                            </div>
                            <input
                              ref="fileInputRef"
                              type="file"
                              accept="image/*,application/pdf"
                              multiple
                              style="display: none"
                              @change="onFileSelect"
                            />
                          </div>

                          <!-- Attachments List -->
                          <div
                            v-if="
                              selectedPerson?.attachments && selectedPerson.attachments.length > 0
                            "
                            class="grid"
                          >
                            <div
                              v-for="attachment in selectedPerson.attachments"
                              :key="attachment.id"
                              class="col-12 mb-3"
                            >
                              <div class="surface-card p-3 border-round shadow-1">
                                <div class="flex align-items-center justify-content-between">
                                  <div class="flex align-items-center">
                                    <template
                                      v-if="
                                        attachment.fileType &&
                                        attachment.fileType.startsWith('image/')
                                      "
                                    >
                                      <img
                                        :src="attachment.url"
                                        alt=""
                                        class="mr-2 border-round"
                                        style="width: 40px; height: 40px; object-fit: cover"
                                      />
                                    </template>
                                    <template v-else>
                                      <i :class="getFileIcon(attachment.fileType)" class="mr-2"></i>
                                    </template>
                                    <div>
                                      <div class="font-medium">{{ attachment.name }}</div>
                                      <div class="text-sm text-500">
                                        {{ attachment.category }} • Added
                                        {{ formatDate(attachment.uploadedAt) }}
                                      </div>
                                    </div>
                                  </div>
                                  <div class="flex gap-2">
                                    <Button
                                      icon="pi pi-eye"
                                      class="p-button-rounded p-button-text"
                                      @click="viewAttachment(attachment)"
                                    />
                                    <Button
                                      icon="pi pi-trash"
                                      class="p-button-rounded p-button-text p-button-danger"
                                      @click="removeAttachment(attachment)"
                                    />
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </Panel>
                </div>

                <div class="col-12">
                  <Panel class="mb-4 company-panel" header="Company">
                    <template #header>
                      <div class="flex align-items-center w-full">
                        <i class="pi pi-building mr-2"></i>
                        <span>Company</span>
                      </div>
                      <div class="ml-auto flex gap-2">
                        <Button
                          v-if="selectedOrganizationId"
                          label="Edit"
                          icon="pi pi-pencil"
                          class="p-button-sm p-button-outlined"
                          @click="openEditOrganizationDialog"
                        />
                        <Button
                          v-if="selectedOrganizationId"
                          label="Clear"
                          icon="pi pi-times"
                          class="p-button-sm p-button-text p-button-danger"
                          @click="clearSelectedOrganization"
                        />
                        <Button
                          v-if="!selectedOrganizationId"
                          label="New"
                          icon="pi pi-plus"
                          class="p-button-sm p-button-outlined"
                          @click="showCreateOrganizationDialog = true"
                        />
                      </div>
                    </template>
                    <div class="grid">
                      <div class="col-12 mb-3">
                        <span class="p-float-label">
                          <AutoComplete
                            v-model="selectedOrganization"
                            :suggestions="filteredOrganizationOptions"
                            @complete="searchOrganizations"
                            @item-select="onOrganizationSelect"
                            field="name"
                            dropdown
                            class="w-full"
                            optionLabel="name"
                          />
                          <label for="organizationSelect">Supplier Company</label>
                        </span>
                      </div>

                      <div
                        v-if="selectedOrganizationId"
                        class="col-12 mb-3 p-2 border-1 surface-border border-round"
                      >
                        <div class="flex flex-column gap-2">
                          <div class="text-lg font-semibold">
                            {{
                              selectedOrganization?.name ||
                              transaction.organizationSnapshot?.name ||
                              'Unknown'
                            }}
                          </div>
                          <div
                            v-if="
                              selectedOrganization?.email || transaction.organizationSnapshot?.email
                            "
                            class="flex align-items-center gap-2"
                          >
                            <i class="pi pi-envelope text-sm"></i>
                            <span>{{
                              selectedOrganization?.email || transaction.organizationSnapshot?.email
                            }}</span>
                          </div>
                          <div
                            v-if="
                              selectedOrganization?.phone || transaction.organizationSnapshot?.phone
                            "
                            class="flex align-items-center gap-2"
                          >
                            <i class="pi pi-phone text-sm"></i>
                            <span>{{
                              selectedOrganization?.phone || transaction.organizationSnapshot?.phone
                            }}</span>
                          </div>
                          <div
                            v-if="
                              selectedOrganization?.address ||
                              transaction.organizationSnapshot?.streetAddress
                            "
                            class="flex align-items-center gap-2"
                          >
                            <i class="pi pi-map-marker text-sm"></i>
                            <span>{{
                              selectedOrganization?.address ||
                              [
                                transaction.organizationSnapshot?.streetAddress,
                                transaction.organizationSnapshot?.city,
                                transaction.organizationSnapshot?.state,
                                transaction.organizationSnapshot?.postalCode,
                              ]
                                .filter(Boolean)
                                .join(', ')
                            }}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </Panel>
                </div>
              </div>

              <div class="flex pt-2 justify-end">
                <Button
                  label="Next"
                  icon="pi pi-arrow-right"
                  iconPos="right"
                  :disabled="!step1Complete"
                  @click="goToStep(2)"
                />
              </div>

              <!-- Person form is now inline in the card -->

              <!-- Organization Dialogs -->
              <OrganizationDialog
                v-model:visible="showCreateOrganizationDialog"
                :organization-data="newOrganization"
                :is-editing="false"
                @organization-saved="onOrganizationCreated"
                @cancel="showCreateOrganizationDialog = false"
              />
              <OrganizationDialog
                v-model:visible="showEditOrganizationDialog"
                :organization-data="editingOrganization"
                :is-editing="true"
                @organization-saved="onOrganizationUpdated"
                @cancel="showEditOrganizationDialog = false"
              />

              <!-- Scanner and results -->
              <IdScanner
                v-model:visible="showIdScanner"
                :personId="selectedPersonId || undefined"
                @document="onIdDocumentSelect"
                @person:new="onPersonNew"
                @person:select="(person) => onPersonSelect({ value: person })"
              />

              <!-- ID Capture Dialog -->
              <IdCaptureDialog
                v-model:visible="showIdCaptureDialog"
                :person="selectedPerson"
                :id-document="currentIdDocument"
                @save="onIdDocumentSaved"
                @cancel="onIdCaptureCancelled"
              />

              <!-- Vehicle Suggestion Dialog -->
              <Dialog
                v-model:visible="showVehicleSuggestionDialog"
                modal
                header="Vehicle Suggestion"
                :style="{ width: '500px' }"
                :closable="false"
              >
                <div v-if="suggestedVehicle" class="flex flex-column gap-4">
                  <div
                    class="flex align-items-center gap-3 p-3 bg-blue-50 border-1 border-blue-200 border-round"
                  >
                    <i class="pi pi-info-circle text-blue-600 text-xl"></i>
                    <div>
                      <div class="font-semibold text-blue-800">
                        Found vehicle from previous transaction
                      </div>
                      <div class="text-sm text-blue-600">
                        Transaction #{{ suggestedVehicle.transactionNumber }} on
                        {{ new Date(suggestedVehicle.transactionDate).toLocaleDateString() }}
                      </div>
                    </div>
                  </div>

                  <div class="p-3 border-1 surface-border border-round">
                    <div class="text-lg font-semibold mb-2">
                      {{ suggestedVehicle.vehicle.tagNumber }}
                      <span v-if="suggestedVehicle.vehicle.state"
                        >({{ suggestedVehicle.vehicle.state }})</span
                      >
                    </div>
                    <div class="grid">
                      <div class="col-6">
                        <strong>Year:</strong> {{ suggestedVehicle.vehicle.year || '—' }}
                      </div>
                      <div class="col-6">
                        <strong>Make:</strong> {{ suggestedVehicle.vehicle.make || '—' }}
                      </div>
                      <div class="col-6">
                        <strong>Model:</strong> {{ suggestedVehicle.vehicle.model || '—' }}
                      </div>
                      <div class="col-6">
                        <strong>Color:</strong> {{ suggestedVehicle.vehicle.color || '—' }}
                      </div>
                      <div class="col-12" v-if="suggestedVehicle.vehicle.vin">
                        <strong>VIN:</strong> {{ suggestedVehicle.vehicle.vin }}
                      </div>
                    </div>
                  </div>

                  <div class="text-center">
                    <p class="text-lg font-medium mb-3">Is this the vehicle they're using today?</p>
                  </div>
                </div>

                <template #footer>
                  <div class="flex justify-content-end gap-2">
                    <Button
                      label="No, Search Different Vehicle"
                      icon="pi pi-search"
                      class="p-button-outlined"
                      @click="declineVehicleSuggestion"
                    />
                    <Button
                      label="Yes, Use This Vehicle"
                      icon="pi pi-check"
                      @click="acceptVehicleSuggestion"
                    />
                  </div>
                </template>
              </Dialog>
            </StepPanel>

            <!-- Step 2: Vehicle Info -->
            <StepPanel :value="2">
              <div class="grid">
                <div class="col-12">
                  <Panel class="mb-4 vehicle-panel" header="Vehicle">
                    <template #header>
                      <div class="flex align-items-center">
                        <i class="pi pi-car mr-2"></i>
                        <span>Vehicle</span>
                      </div>
                      <div class="ml-auto flex gap-2">
                        <Button
                          v-if="selectedVehicle"
                          label="Edit"
                          icon="pi pi-pencil"
                          class="p-button-sm p-button-outlined"
                          @click="editSelectedVehicle"
                        />
                        <Button
                          v-if="selectedVehicle"
                          label="Clear"
                          icon="pi pi-times"
                          class="p-button-sm p-button-text p-button-danger"
                          @click="clearSelectedVehicle"
                        />
                        <Button
                          v-if="!selectedVehicle"
                          label="New"
                          icon="pi pi-plus"
                          class="p-button-sm p-button-outlined"
                          @click="startCreateVehicle"
                        />
                      </div>
                    </template>

                    <!-- Validation Status -->
                    <div
                      v-if="selectedVehicle && !satisfyVehicleRules()"
                      class="mb-3 p-3 border-1 border-orange-200 surface-orange-50 border-round"
                    >
                      <div class="flex align-items-center gap-2 mb-2">
                        <i class="pi pi-exclamation-triangle text-orange-600"></i>
                        <span class="font-semibold text-orange-700"
                          >Missing Required Information</span
                        >
                      </div>
                      <div class="text-sm text-orange-600">
                        <ul class="m-0 pl-3">
                          <li v-for="error in getVehicleValidationErrors()" :key="error">
                            {{ error }}
                          </li>
                        </ul>
                      </div>
                    </div>

                    <!-- Vehicle Form -->
                    <div v-if="showVehicleForm">
                      <div class="grid">
                        <div
                          v-for="field in enabledVehicleFields"
                          :key="field.key"
                          :class="getVehicleFieldColumnClass(field.key)"
                        >
                          <span class="p-float-label">
                            <InputText
                              :id="field.key"
                              v-model="(vehicleForm as any)[field.key]"
                              class="w-full"
                            />
                            <label :for="field.key">
                              {{ field.label }}
                            </label>
                          </span>
                        </div>
                      </div>

                      <div class="flex justify-content-end gap-2 pt-3 border-top-1 surface-border">
                        <Button
                          label="Cancel"
                          icon="pi pi-times"
                          class="p-button-text"
                          @click="cancelVehicleForm"
                        />
                        <Button
                          :label="isEditingVehicle ? 'Update' : 'Save'"
                          icon="pi pi-check"
                          @click="saveVehicleForm"
                          :loading="savingVehicle"
                        />
                      </div>
                    </div>

                    <!-- Vehicle Search / Summary -->
                    <div v-else>
                      <div v-if="!selectedVehicle" class="h-full">
                        <div class="grid h-full">
                          <div class="col-12 flex flex-column p-4">
                            <div class="flex align-items-center justify-content-between mb-4">
                              <h4 class="m-0">Search for Vehicle</h4>
                            </div>
                            <div class="flex gap-2 items-center mb-3">
                              <InputText
                                id="searchTag"
                                v-model="vehicleTagSearch"
                                class="flex-1"
                                placeholder="Enter tag number"
                                @keydown.enter="searchVehicles"
                              />
                              <Button
                                label="Search"
                                icon="pi pi-search"
                                @click="searchVehicles"
                                :loading="vehicleSearching"
                              />
                            </div>
                            <div v-if="vehicleSearchResults.length > 0" class="mb-4">
                              <div class="grid">
                                <div
                                  v-for="v in vehicleSearchResults"
                                  :key="v.id"
                                  class="col-12 mb-2"
                                >
                                  <div
                                    class="p-3 border-1 surface-border border-round cursor-pointer hover:surface-100"
                                    @click="onVehicleSelect(v)"
                                  >
                                    <div class="flex align-items-center justify-content-between">
                                      <div>
                                        <div class="font-semibold">
                                          {{ v.tagNumber }}
                                          <span v-if="v.state">({{ v.state }})</span>
                                        </div>
                                        <div class="text-sm text-gray-600">
                                          {{ v.year || '—' }} {{ v.make || '' }}
                                          {{ v.model || '' }} {{ v.color || '' }}
                                        </div>
                                      </div>
                                      <i class="pi pi-arrow-right text-gray-400"></i>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>

                            <div
                              v-else-if="
                                vehicleSearchPerformed && vehicleTagSearch && !vehicleSearching
                              "
                              class="text-center mb-4"
                            >
                              <p class="text-gray-600 mb-4">
                                No vehicles found matching your search.
                              </p>
                              <Button
                                label="Create New Vehicle"
                                icon="pi pi-plus"
                                class="p-button-outlined"
                                @click="startCreateVehicle"
                              />
                            </div>

                            <div v-else class="text-center">
                              <p class="text-gray-600 mb-4">Enter a tag number to search</p>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div v-else class="h-full">
                        <div
                          class="flex flex-column gap-2 p-2 border-1 surface-border border-round"
                        >
                          <div class="text-lg font-semibold">
                            {{ selectedVehicle.tagNumber }}
                            <span v-if="selectedVehicle.state">({{ selectedVehicle.state }})</span>
                          </div>
                          <div class="flex flex-wrap gap-3 text-sm">
                            <div><strong>Year:</strong> {{ selectedVehicle.year || '—' }}</div>
                            <div><strong>Make:</strong> {{ selectedVehicle.make || '—' }}</div>
                            <div><strong>Model:</strong> {{ selectedVehicle.model || '—' }}</div>
                            <div><strong>Color:</strong> {{ selectedVehicle.color || '—' }}</div>
                            <div><strong>VIN:</strong> {{ selectedVehicle.vin || '—' }}</div>
                          </div>
                          <div v-if="selectedVehicle.notes" class="text-sm text-gray-600">
                            <strong>Notes:</strong> {{ selectedVehicle.notes }}
                          </div>
                        </div>
                      </div>
                    </div>
                  </Panel>
                </div>
              </div>

              <div class="flex pt-6 justify-between">
                <Button
                  label="Back"
                  severity="secondary"
                  icon="pi pi-arrow-left"
                  @click="goToStep(1)"
                />
                <Button
                  label="Next"
                  icon="pi pi-arrow-right"
                  iconPos="right"
                  :disabled="!step2Complete"
                  @click="goToStep(3)"
                />
              </div>
            </StepPanel>

            <!-- Step 3: Items -->
            <StepPanel :value="3">
              <!-- Validation Status for Items -->
              <div
                v-if="(transaction.items || []).length > 0 && !step3Complete"
                class="mb-3 p-3 border-1 border-orange-200 surface-orange-50 border-round"
              >
                <div class="flex align-items-center gap-2 mb-2">
                  <i class="pi pi-exclamation-triangle text-orange-600"></i>
                  <span class="font-semibold text-orange-700"
                    >{{ itemsValidation.invalidIndexes.length }}
                    {{ itemsValidation.invalidIndexes.length === 1 ? 'Item' : 'Items' }} Missing
                    Required Information</span
                  >
                </div>
              </div>

              <TransactionItemPhotoList
                :items="transaction.items || []"
                :commodity-types="commodityTypes"
                @add-item="addNewItem"
                @edit-item="editItem"
                @remove-item="removeItem"
                @validate="(p) => (itemsValidation = p)"
              />
              <TransactionItemFormDialog
                v-model:visible="showItemDialog"
                :commodity-types="commodityTypes"
                :item-index="editingItemIndex"
                @save="onItemSaved"
                @cancel="onItemCancelled"
              />
              <div class="flex pt-6 justify-between">
                <Button
                  label="Back"
                  severity="secondary"
                  icon="pi pi-arrow-left"
                  @click="goToStep(2)"
                />
                <Button
                  label="Next"
                  icon="pi pi-arrow-right"
                  iconPos="right"
                  :disabled="!step3Complete"
                  @click="goToStep(4)"
                />
              </div>
            </StepPanel>

            <!-- Step 4: Signature -->
            <StepPanel :value="4">
              <div class="grid">
                <!-- Supplier Questionnaire Panel (only for new people) -->
                <div v-if="showSupplierQuestionnaireStep" class="col-12">
                  <Panel class="mb-4 questionnaire-panel" header="New Supplier Information">
                    <template #header>
                      <div class="flex align-items-center">
                        <i class="pi pi-question-circle mr-2"></i>
                        <span>New Supplier Information</span>
                      </div>
                    </template>

                    <div class="flex flex-column gap-4">
                      <div class="text-center mb-4">
                        <i class="pi pi-info-circle text-4xl text-blue-500 mb-3"></i>
                        <h3 class="text-xl font-semibold mb-2">
                          Welcome{{ companyName ? ` to ${companyName}` : '' }}!
                        </h3>
                        <p class="text-gray-600">
                          We'd love to know how you heard about our scrap metal yard. This helps us
                          improve our services and marketing efforts.
                        </p>
                      </div>

                      <div class="field">
                        <label class="block mb-3 font-semibold"
                          >How did you hear about our scrap metal yard?</label
                        >
                        <div class="flex flex-column gap-3">
                          <div class="flex align-items-center">
                            <RadioButton
                              v-model="questionnaireData.referralSource"
                              inputId="referral"
                              value="referral"
                              name="referralSource"
                            />
                            <label for="referral" class="ml-2">Referral (Friend/Colleague)</label>
                          </div>

                          <div class="flex align-items-center">
                            <RadioButton
                              v-model="questionnaireData.referralSource"
                              inputId="online-search"
                              value="online-search"
                              name="referralSource"
                            />
                            <label for="online-search" class="ml-2"
                              >Online Search (maps or search)</label
                            >
                          </div>

                          <div class="flex align-items-center">
                            <RadioButton
                              v-model="questionnaireData.referralSource"
                              inputId="social-media"
                              value="social-media"
                              name="referralSource"
                            />
                            <label for="social-media" class="ml-2">Social Media</label>
                          </div>

                          <div
                            v-if="questionnaireData.referralSource === 'social-media'"
                            class="ml-6"
                          >
                            <InputText
                              v-model="questionnaireData.socialMediaDetails"
                              placeholder="Please specify: Facebook, Instagram, etc."
                              class="w-full"
                            />
                          </div>

                          <div class="flex align-items-center">
                            <RadioButton
                              v-model="questionnaireData.referralSource"
                              inputId="passing-by"
                              value="passing-by"
                              name="referralSource"
                            />
                            <label for="passing-by" class="ml-2">Passing By/Signage</label>
                          </div>

                          <div class="flex align-items-center">
                            <RadioButton
                              v-model="questionnaireData.referralSource"
                              inputId="industry-publication"
                              value="industry-publication"
                              name="referralSource"
                            />
                            <label for="industry-publication" class="ml-2"
                              >Industry Publication/Trade Show</label
                            >
                          </div>

                          <div class="flex align-items-center">
                            <RadioButton
                              v-model="questionnaireData.referralSource"
                              inputId="existing-customer"
                              value="existing-customer"
                              name="referralSource"
                            />
                            <label for="existing-customer" class="ml-2"
                              >Existing Customer (We've sold to you)</label
                            >
                          </div>

                          <div class="flex align-items-center">
                            <RadioButton
                              v-model="questionnaireData.referralSource"
                              inputId="other"
                              value="other"
                              name="referralSource"
                            />
                            <label for="other" class="ml-2">Other</label>
                          </div>

                          <div v-if="questionnaireData.referralSource === 'other'" class="ml-6">
                            <InputText
                              v-model="questionnaireData.otherDetails"
                              placeholder="Please specify"
                              class="w-full"
                            />
                          </div>
                        </div>
                      </div>

                      <div class="field">
                        <label class="block mb-2 font-semibold"
                          >Additional Comments (Optional)</label
                        >
                        <Textarea
                          v-model="questionnaireData.additionalComments"
                          placeholder="Any other feedback or comments..."
                          rows="3"
                          class="w-full"
                        />
                      </div>
                    </div>
                  </Panel>
                </div>

                <div class="col-12">
                  <Panel class="mb-4 signature-panel" header="Supplier Signature">
                    <template #header>
                      <div class="flex align-items-center">
                        <i class="pi pi-pencil mr-2"></i>
                        <span>Supplier Signature</span>
                      </div>
                    </template>

                    <div class="signature-preview">
                      <div v-if="transaction.party_signature" class="signature-image">
                        <img :src="transaction.party_signature.data_url" alt="Party Signature" />
                        <small>
                          Signed:
                          {{
                            transaction.party_signature.timestamp &&
                            Object.keys(transaction.party_signature.timestamp).length > 0
                              ? transaction.party_signature.timestamp.toDateString()
                              : transactionStore.original?.party_signature?.timestamp
                                ? transactionStore.original.party_signature.timestamp.toDateString()
                                : 'Date unknown'
                          }}
                        </small>
                      </div>
                      <div v-else class="signature-placeholder">
                        <i class="pi pi-user"></i>
                        <span>No supplier signature</span>
                      </div>
                    </div>
                    <Button
                      :label="'Capture Supplier Signature'"
                      icon="pi pi-pencil"
                      class="p-button-outlined mt-2 w-full"
                      @click="showSignatureDialog = true"
                    />
                  </Panel>
                </div>
              </div>
              <SignatureDialog
                v-model="showSignatureDialog"
                title="Declaration of Ownership"
                :subtitle="transactionModel?.party.name || ''"
                :transaction-type="transaction.transaction_type"
                :show-legal-declaration="true"
                @save="savePartySignature"
              />
              <div class="flex pt-6 justify-between">
                <Button
                  label="Back"
                  severity="secondary"
                  icon="pi pi-arrow-left"
                  @click="goToStep(3)"
                />
                <Button
                  label="Next"
                  icon="pi pi-arrow-right"
                  iconPos="right"
                  :disabled="!step4Complete"
                  @click="goToStep(5)"
                />
              </div>
            </StepPanel>

            <!-- Step 5: Review & Submit -->
            <StepPanel :value="5">
              <div class="grid">
                <div class="col-12">
                  <Panel
                    class="mb-4 review-panel border-green-200 surface-card"
                    header="Transaction Summary"
                  >
                    <template #header>
                      <div class="flex align-items-center">
                        <i class="pi pi-calculator mr-2"></i>
                        <span class="text-green-700">Transaction Summary</span>
                      </div>
                    </template>

                    <div class="grid">
                      <div class="col-12 md:col-6">
                        <div class="text-lg mb-2">
                          Total Items:
                          <span class="font-semibold">{{ (transaction.items || []).length }}</span>
                        </div>
                        <div class="text-lg mb-2">
                          Total Weight:
                          <span class="font-semibold"
                            >{{ transactionModel?.totalWeight || 0 }} lbs</span
                          >
                        </div>
                      </div>
                      <div class="col-12 md:col-6 text-right">
                        <div class="text-2xl text-green-600 font-bold">
                          Total Amount:
                          {{
                            TransactionModel.formatCurrency(
                              parseFloat(transactionModel?.totalAmount || '0'),
                            )
                          }}
                        </div>
                      </div>
                    </div>
                  </Panel>
                </div>
              </div>
              <div class="flex pt-6 justify-between">
                <Button
                  label="Back"
                  severity="secondary"
                  icon="pi pi-arrow-left"
                  @click="goToStep(4)"
                />
                <Button label="Submit Purchase" icon="pi pi-check" @click="submitWizard" />
              </div>
            </StepPanel>
          </StepPanels>
        </Stepper>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useToast } from 'primevue/usetoast'
import { useConfirm } from 'primevue/useconfirm'

// Components
import Stepper from 'primevue/stepper'
import StepList from 'primevue/steplist'
import Step from 'primevue/step'
import StepPanels from 'primevue/steppanels'
import StepPanel from 'primevue/steppanel'
import Divider from 'primevue/divider'
import Card from 'primevue/card'
import Panel from 'primevue/panel'
import Button from 'primevue/button'
import AutoComplete from 'primevue/autocomplete'
import InputText from 'primevue/inputtext'
import InputMask from 'primevue/inputmask'
import Badge from 'primevue/badge'
import RadioButton from 'primevue/radiobutton'
import Textarea from 'primevue/textarea'
import OrganizationDialog from '@/components/organization/OrganizationDialog.vue'

// Vehicle step utilities
import { useVehicleStore } from '@/stores/vehicleStore'
import type { Vehicle } from '@/types/Vehicle'
import TransactionItemFormDialog from '@/components/transaction/TransactionItemFormDialog.vue'
import { formatDate } from '@/utils/formatters'
import TransactionItemPhotoList from '@/components/TransactionItemPhotoList.vue'
import SignatureDialog from '@/components/SignatureDialog.vue'
import IdScanner from '@/components/common/IdScanner.vue'
import IdCaptureDialog from '@/components/common/IdCaptureDialog.vue'
import { useIDDocumentStore } from '@/stores/idDocumentStore'
import { IDDocumentModel } from '@/models/IDDocumentModel'

// Services
import { useInitializationService } from '@/services/initializationService'
import { usePersonStore } from '@/stores/v2/person'
import { useOrganizationStore } from '@/stores/v2/organization'
import { useTransactionService } from '@/services/transactionService'
import { vehicleService } from '@/services/vehicleService'
import { idDocumentService } from '@/services/idDocumentService'

// Stores / models / types
import { useSettingsStore } from '@/stores/settings'
import { createPerson } from '@/factories/personFactory'
import { createOrganization } from '@/factories/organizationFactory'
import { useTransactionStore } from '@/stores/transactionStoreOld'
import { TransactionModel } from '@/models/TransactionModel'
import { firestoreService } from '@/utils/firestoreService'
import type { Person } from '@/types/Person'
import type { Organization } from '@/types/Organization'
import type { CommodityType } from '@/types/CommodityType'
import type { TransactionRecord } from '@/types/Transaction'
import type { IDDocument } from '@/types/IDDocument'

const toast = useToast()
const confirm = useConfirm()
const router = useRouter()
const route = useRoute()

// Services
const initializationService = useInitializationService()
const personStore = usePersonStore()
const organizationStore = useOrganizationStore()
const transactionService = useTransactionService()

// Stores
const transactionStore = useTransactionStore()
const vehicleStore = useVehicleStore()
const idDocumentStore = useIDDocumentStore()
const settingsStore = useSettingsStore()

// Utility functions
function validatePersonForTransaction(person: Person): { isValid: boolean; errors: string[] } {
  const errors: string[] = []

  if (!person.firstName?.trim()) {
    errors.push('First name is required')
  }

  if (!person.lastName?.trim()) {
    errors.push('Last name is required')
  }

  return {
    isValid: errors.length === 0,
    errors,
  }
}

// Wizard state
const activeStep = ref(1)
function goToStep(step: number) {
  activeStep.value = step
}

// Local UI state
const showCreateOrganizationDialog = ref(false)
const showEditOrganizationDialog = ref(false)
const showSignatureDialog = ref(false)
const showItemDialog = ref(false)
const showIdCaptureDialog = ref(false)
const showVehicleSuggestionDialog = ref(false)
const editingItemIndex = ref(-1)
const commodityTypes = ref<CommodityType[]>([])
const selectedPerson = ref<(Person & { displayName?: string }) | null>(null)
const selectedOrganization = ref<Organization | null>(null)
const filteredPeopleOptions = ref<Array<Person & { displayName: string }>>([])
const filteredOrganizationOptions = ref<Organization[]>([])
const editingOrganization = ref<Partial<Organization>>({})
const selectedPersonId = ref<string>('')
const selectedOrganizationId = ref<string>('')
const selectedVehicleId = ref<string>('')

// Inline phone editing state
const isEditingPhone = ref(false)
const inlinePhoneValue = ref('')
const savingPhone = ref(false)

// Loading state to prevent multiple simultaneous loads
const isInitializing = ref(false)

// New Person card state
const showPersonForm = ref(false)
const showManualSearch = ref(false)
const showFullSearch = ref(false)
const isEditingPerson = ref(false)
const personFormSubmitted = ref(false)
const savingPerson = ref(false)
const personForm = ref<Partial<Person>>({
  firstName: '',
  lastName: '',
  middleName: '',
  idNumber: '',
  email: '',
  phone: '',
  streetAddress: '',
  city: '',
  state: '',
  postalCode: '',
  country: '',
  type: 'supplier' as any,
  status: 'supplier',
})

// Manual search state
const searchFirstName = ref('')
const searchLastName = ref('')
const manualSearchResults = ref<Person[]>([])
const manualSearchPerformed = ref(false)
const showAllSearchResults = ref(false)

// Scanner state
const showIdScanner = ref(false)

// Supplier questionnaire state
const questionnairePersonId = ref<string>('')
const showSupplierQuestionnaireStep = computed(() => {
  // Show questionnaire if person is "new" (status is not "active")
  if (!selectedPerson.value) return false
  return selectedPerson.value.status !== 'supplier'
})
const questionnaireData = ref({
  referralSource: '',
  socialMediaDetails: '',
  otherDetails: '',
  additionalComments: '',
})

// Computed
const isDev = computed(() => import.meta.env.DEV)
const transactionId = computed(() => route.params.id as string)
const transaction = computed(
  () => transactionStore.draft || ({ items: [], transaction_type: 'purchase' } as any),
)

const transactionModel = computed(() => {
  if (transaction.value) {
    return new TransactionModel(transaction.value)
  }
  return null
})

// Get current ID document for the selected person
const currentIdDocument = ref<IDDocument | null>(null)

// Load current ID document for selected person
async function loadCurrentIdDocument() {
  if (!selectedPerson.value?.currentIdDocumentId) {
    currentIdDocument.value = null
    return
  }

  try {
    const doc = await idDocumentService.get(selectedPerson.value.currentIdDocumentId)
    currentIdDocument.value = doc || null
  } catch (error) {
    console.error('Failed to load current ID document:', error)
    currentIdDocument.value = null
  }
}

// Open ID capture dialog
async function openIdCaptureDialog() {
  await loadCurrentIdDocument()
  showIdCaptureDialog.value = true
}

// Person validation rules
function satisfyPersonRules(): boolean {
  // If no person is selected, rules are satisfied (organization-only transactions are valid)
  if (!selectedPersonId.value) {
    return true
  }

  const person = selectedPerson.value
  if (!person) {
    return false
  }

  const hasIdPhoto = !!transactionModel.value?.party.idInfo.image
  const validation = validatePersonForTransaction(person)

  // Check phone number requirement if enabled in settings
  if (settingsStore.transactionSettings.requirePhoneNumber && !person.phone) {
    return false
  }

  return validation.isValid
}

function getPersonValidationErrors(): string[] {
  if (!selectedPersonId.value) {
    return []
  }

  const person = selectedPerson.value
  if (!person) {
    return ['Person data not found']
  }

  const hasIdPhoto = !!transactionModel.value?.party.idInfo.image
  const validation = validatePersonForTransaction(person)
  const errors = [...validation.errors]

  // Add phone number requirement error if enabled in settings
  if (settingsStore.transactionSettings.requirePhoneNumber && !person.phone) {
    errors.push('Phone number is required')
  }

  return errors
}

// Step completion logic
const step1Complete = computed(() => {
  const hasSelection = !!(selectedPersonId.value || selectedOrganizationId.value)
  if (!hasSelection) {
    return false
  }

  // If a person is selected, validate person rules
  if (selectedPersonId.value) {
    return satisfyPersonRules()
  }

  // Organization-only transactions are valid
  return true
})
function satisfyVehicleRules(): boolean {
  const vs = transaction.value?.vehicleSnapshot as Partial<Vehicle> | undefined
  if (!vs) return false

  const validation = vehicleService.validateVehicleForTransaction(vs)
  return validation.isValid
}

function getVehicleValidationErrors(): string[] {
  const vs = transaction.value?.vehicleSnapshot as Partial<Vehicle> | undefined
  if (!vs) {
    return ['Vehicle selection is required']
  }

  const validation = vehicleService.validateVehicleForTransaction(vs)
  return validation.errors
}
const step2Complete = computed(() => satisfyVehicleRules())
const itemsValidation = ref<{
  valid: boolean
  invalidIndexes: number[]
  errorsByIndex: string[][]
}>({
  valid: false,
  invalidIndexes: [],
  errorsByIndex: [],
})
const step3Complete = computed(() => itemsValidation.value.valid)
const step4Complete = computed(() => {
  // For new people, require both signature and questionnaire completion
  if (showSupplierQuestionnaireStep.value) {
    return (
      !!transaction.value?.party_signature?.data_url && !!questionnaireData.value.referralSource
    )
  }
  // For existing people, only require signature
  return !!transaction.value?.party_signature?.data_url
})
const step5Ready = computed(() => {
  return step1Complete.value && step2Complete.value && step3Complete.value && step4Complete.value
})
const allStepsComplete = step5Ready

// Vehicle field configuration
const enabledVehicleFields = computed(() => {
  return settingsStore.transactionSettings.vehicleFieldConfig.filter((field) => field.enabled)
})

const companyName = computed(() => settingsStore.companySettings.name)

function getVehicleFieldColumnClass(fieldKey: string): string {
  // Special layout for certain fields
  if (fieldKey === 'notes') return 'col-12 mb-3'
  return 'col-12 md:col-6 mb-3'
}

const newPerson = ref(createPerson({ status: 'supplier', type: 'supplier' as any }))
const newOrganization = ref(createOrganization({ type: 'supplier', status: 'active' }))

// Methods
// Vehicle step state and methods
const vehicleTagSearch = ref('')
const vehicleSearchResults = ref<Vehicle[]>([])
const vehicleSearching = ref(false)
const vehicleSearchPerformed = ref(false)
const selectedVehicle = ref<Vehicle | null>(null)
const showVehicleForm = ref(false)
const isEditingVehicle = ref(false)
const savingVehicle = ref(false)
const vehicleForm = ref<Partial<Vehicle>>({
  tagNumber: '',
  state: '',
  year: undefined,
  make: '',
  model: '',
  color: '',
  vin: '',
  notes: '',
  createdAt: new Date(),
  updatedAt: new Date(),
})

// Vehicle suggestion state
const suggestedVehicle = ref<{
  vehicle: Vehicle
  transactionDate: Date
  transactionNumber: string
} | null>(null)

async function searchVehicles() {
  if (!vehicleTagSearch.value.trim()) return
  vehicleSearchPerformed.value = true
  vehicleSearching.value = true
  try {
    vehicleSearchResults.value = await vehicleService.searchByTag(vehicleTagSearch.value.trim())
  } finally {
    vehicleSearching.value = false
  }
}

async function onVehicleSelect(vehicle: Vehicle) {
  selectedVehicle.value = vehicle
  selectedVehicleId.value = vehicle.id || ''
  vehicleTagSearch.value = vehicle.tagNumber || ''
  if (transaction.value?.id) {
    await transactionStore.updateDraft({
      vehicleId: vehicle.id || '',
      vehicleSnapshot: {
        tagNumber: vehicle.tagNumber,
        state: vehicle.state,
        year: vehicle.year,
        make: vehicle.make,
        model: vehicle.model,
        color: vehicle.color,
        vin: vehicle.vin,
        notes: vehicle.notes,
      },
    })
  }
  toast.add({
    severity: 'success',
    summary: 'Vehicle Selected',
    detail: vehicle.tagNumber,
    life: 2000,
  })
}

function startCreateVehicle() {
  isEditingVehicle.value = false
  showVehicleForm.value = true
  vehicleForm.value = {
    tagNumber: vehicleTagSearch.value.trim(),
    state: '',
    year: undefined,
    make: '',
    model: '',
    color: '',
    vin: '',
    notes: '',
    createdAt: new Date(),
    updatedAt: new Date(),
  }
}

function editSelectedVehicle() {
  if (!selectedVehicle.value) return
  isEditingVehicle.value = true
  showVehicleForm.value = true
  vehicleForm.value = { ...selectedVehicle.value }
}

function cancelVehicleForm() {
  showVehicleForm.value = false
}

async function saveVehicleForm() {
  savingVehicle.value = true
  try {
    if (isEditingVehicle.value && vehicleForm.value.id) {
      const result = await vehicleService.updateVehicle(vehicleForm.value.id, vehicleForm.value)
      if (result.success) {
        await onVehicleSelect(result.vehicle)
        showVehicleForm.value = false
      } else {
        toast.add({
          severity: 'error',
          summary: 'Validation Error',
          detail: result.errors.join(', '),
          life: 5000,
        })
      }
    } else {
      const result = await vehicleService.createVehicle(vehicleForm.value)
      if (result.success) {
        await onVehicleSelect(result.vehicle)
        showVehicleForm.value = false
      } else {
        toast.add({
          severity: 'error',
          summary: 'Validation Error',
          detail: result.errors.join(', '),
          life: 5000,
        })
      }
    }
  } catch (e) {
    console.error('Error saving vehicle:', e)
    toast.add({ severity: 'error', summary: 'Error', detail: 'Failed to save vehicle', life: 3000 })
  } finally {
    savingVehicle.value = false
  }
}

function clearSelectedVehicle() {
  selectedVehicle.value = null
  selectedVehicleId.value = ''
  vehicleTagSearch.value = ''
  vehicleSearchResults.value = []
  vehicleSearchPerformed.value = false
  if (transaction.value?.id) {
    transactionStore.updateDraft({
      vehicleId: '',
      vehicleSnapshot: undefined,
    })
  }
  toast.add({
    severity: 'info',
    summary: 'Cleared',
    detail: 'Vehicle selection cleared',
    life: 2000,
  })
}

async function suggestVehicleFromRecentTransaction(personId: string) {
  try {
    // Only suggest if no vehicle is already selected
    if (selectedVehicle.value) {
      return
    }

    // Query for the most recent transaction by this person using firestoreService
    const recentTransactions = await firestoreService.query<TransactionRecord>(
      'transaction_records',
      [
        { field: 'personId', op: '==', value: personId },
        { field: 'is_deleted', op: '==', value: false },
      ],
      ['transaction_date', 'desc'],
      1, // Limit to 1 most recent transaction
    )

    if (recentTransactions.length > 0) {
      const recentTransaction = recentTransactions[0]

      // Check if the recent transaction has a vehicle
      if (recentTransaction.vehicleId && recentTransaction.vehicleSnapshot) {
        // Show suggestion dialog instead of auto-selecting
        const vehicle = recentTransaction.vehicleSnapshot
        suggestedVehicle.value = {
          vehicle: {
            id: recentTransaction.vehicleId,
            tagNumber: vehicle.tagNumber,
            state: vehicle.state,
            year: vehicle.year,
            make: vehicle.make,
            model: vehicle.model,
            color: vehicle.color,
            vin: vehicle.vin,
            notes: vehicle.notes,
            createdAt: new Date(),
            updatedAt: new Date(),
          } as Vehicle,
          transactionDate: recentTransaction.transaction_date,
          transactionNumber: recentTransaction.transaction_number,
        }
        showVehicleSuggestionDialog.value = true
      }
    }
  } catch (error) {
    console.error('Error suggesting vehicle from recent transaction:', error)
    // Don't show error toast as this is a convenience feature
  }
}

async function acceptVehicleSuggestion() {
  if (!suggestedVehicle.value) return

  const vehicle = suggestedVehicle.value.vehicle
  selectedVehicle.value = vehicle
  selectedVehicleId.value = vehicle.id || ''
  vehicleTagSearch.value = vehicle.tagNumber || ''

  // Update the transaction draft
  if (transaction.value?.id) {
    await transactionStore.updateDraft({
      vehicleId: vehicle.id || '',
      vehicleSnapshot: {
        tagNumber: vehicle.tagNumber,
        state: vehicle.state,
        year: vehicle.year,
        make: vehicle.make,
        model: vehicle.model,
        color: vehicle.color,
        vin: vehicle.vin,
        notes: vehicle.notes,
      },
    })
  }

  // Close dialog and clear suggestion
  showVehicleSuggestionDialog.value = false
  suggestedVehicle.value = null

  toast.add({
    severity: 'success',
    summary: 'Vehicle Selected',
    detail: `Using vehicle from previous transaction: ${vehicle.tagNumber}`,
    life: 3000,
  })
}

function declineVehicleSuggestion() {
  showVehicleSuggestionDialog.value = false
  suggestedVehicle.value = null
  // User can now search for a different vehicle
}
async function searchPeople(event: { query: string }) {
  const q = (event.query || '').toLowerCase()
  const search = await personStore.search()
  const searchResults = await search.byName(q, q).run()
  filteredPeopleOptions.value = searchResults.map((person) => ({
    ...person,
    displayName: `${person.firstName}${person.middleName ? ' ' + person.middleName : ''} ${person.lastName}`,
  }))
}

async function searchOrganizations(event: { query: string }) {
  const q = (event.query || '').toLowerCase()
  const search = await organizationStore.search()
  const searchResults = await search.byText(q).run()
  filteredOrganizationOptions.value = searchResults
}

async function onPersonSelect(event: { value: Person & { displayName?: string } }) {
  const person = event.value
  if (!person.id) {
    console.error('Person has no id')
    return
  }
  selectedPersonId.value = person.id
  selectedPerson.value = person
  showIdScanner.value = false

  // Check if person is missing required fields and open form if needed
  const validation = validatePersonForTransaction(person)
  const isMissingPhone = settingsStore.transactionSettings.requirePhoneNumber && !person.phone

  // Only open the form for serious validation issues, not just missing phone
  // Phone can be added inline, so we don't need to open the full form
  if (!validation.isValid && !isMissingPhone) {
    console.log(
      '[PurchaseWizard] Person missing required fields (excluding phone), opening edit form',
    )
    editSelectedPerson()
    return
  }

  if (transaction.value?.id) {
    await transactionStore.updateDraft({
      personId: person.id,
      personSnapshot: {
        firstName: person.firstName || '',
        middleName: person.middleName || '',
        lastName: person.lastName || '',
        email: person.email || '',
        phone: person.phone || '',
        streetAddress: person.streetAddress || '',
        city: person.city || '',
        state: person.state || '',
        postalCode: person.postalCode || '',
        country: person.country || '',
        idNumber: person.idNumber || '',
        photoIdUrl: person.photoIdUrl || '',
      },
    })
  }

  if (person.organizationId) {
    const org = await organizationStore.get(person.organizationId)
    if (org && org.id) {
      selectedOrganization.value = org
      selectedOrganizationId.value = org.id
      await onOrganizationSelect({ value: org })
    }
  }

  // Auto-suggest vehicle from most recent transaction
  await suggestVehicleFromRecentTransaction(person.id)

  // Load current ID document for the selected person
  await loadCurrentIdDocument()
}

async function onOrganizationSelect(event: { value: Organization }) {
  const org = event.value
  if (!org.id) {
    console.error('Organization has no id')
    return
  }
  selectedOrganizationId.value = org.id
  selectedOrganization.value = org
  if (transaction.value?.id) {
    await transactionStore.updateDraft({
      organizationId: org.id,
      organizationSnapshot: {
        name: org.name || '',
        phone: org.phone || '',
        email: org.email || '',
        streetAddress: org.address || '',
        city: org.city || '',
        state: org.state || '',
        postalCode: org.postalCode || '',
        country: org.country || '',
      },
    })
  }
}

async function onPersonCreated(createdPerson: Person) {
  const personId = await personStore.create(createdPerson)
  selectedPersonId.value = personId
  // Get the created person to set selectedPerson
  const created = await personStore.get(personId)
  if (created) {
    selectedPerson.value = {
      ...created,
      displayName: `${created.firstName}${created.middleName ? ' ' + created.middleName : ''} ${created.lastName}`,
    }
  }
  if (transaction.value?.id) {
    await transactionStore.updateDraft({
      personId: personId,
      personSnapshot: {
        firstName: createdPerson.firstName || '',
        middleName: createdPerson.middleName || '',
        lastName: createdPerson.lastName || '',
        email: createdPerson.email || '',
        phone: createdPerson.phone || '',
        streetAddress: createdPerson.streetAddress || '',
        city: createdPerson.city || '',
        state: createdPerson.state || '',
        postalCode: createdPerson.postalCode || '',
        country: createdPerson.country || '',
        idNumber: createdPerson.idNumber || '',
        photoIdUrl: createdPerson.photoIdUrl || '',
      },
    })
  }

  // Auto-suggest vehicle from most recent transaction (though new person won't have any)
  await suggestVehicleFromRecentTransaction(personId)

  toast.add({
    severity: 'success',
    summary: 'Success',
    detail: 'Contact created successfully',
    life: 3000,
  })
}

async function onOrganizationCreated(createdOrg: Organization) {
  const orgId = await organizationStore.create(createdOrg)
  selectedOrganizationId.value = orgId
  // Get the created organization to set selectedOrganization
  const created = await organizationStore.get(orgId)
  if (created) {
    selectedOrganization.value = created
  }
  if (transaction.value?.id) {
    await transactionStore.updateDraft({
      organizationId: orgId,
      organizationSnapshot: {
        name: createdOrg.name || '',
        phone: createdOrg.phone || '',
        email: createdOrg.email || '',
        streetAddress: createdOrg.address || '',
        city: createdOrg.city || '',
        state: createdOrg.state || '',
        postalCode: createdOrg.postalCode || '',
        country: createdOrg.country || '',
      },
    })
  }
  toast.add({
    severity: 'success',
    summary: 'Success',
    detail: 'Company created successfully',
    life: 3000,
  })
  showCreateOrganizationDialog.value = false
}

async function onPersonUpdated(updatedPerson: Person) {
  if (!updatedPerson.id) {
    console.error('Updated person has no id')
    return
  }
  await personStore.update(updatedPerson.id, updatedPerson)

  // Update the selected person reference if this is the currently selected person
  if (selectedPersonId.value === updatedPerson.id) {
    selectedPerson.value = {
      ...updatedPerson,
      displayName: `${updatedPerson.firstName}${updatedPerson.middleName ? ' ' + updatedPerson.middleName : ''} ${updatedPerson.lastName}`,
    }
  }

  if (selectedPersonId.value === updatedPerson.id && transaction.value?.id) {
    transactionStore.updateDraft({
      personSnapshot: {
        firstName: updatedPerson.firstName || '',
        middleName: updatedPerson.middleName || '',
        lastName: updatedPerson.lastName || '',
        email: updatedPerson.email || '',
        phone: updatedPerson.phone || '',
        streetAddress: updatedPerson.streetAddress || '',
        city: updatedPerson.city || '',
        state: updatedPerson.state || '',
        postalCode: updatedPerson.postalCode || '',
        country: updatedPerson.country || '',
        idNumber: updatedPerson.idNumber || '',
        photoIdUrl: updatedPerson.photoIdUrl || '',
      },
    })
  }
  if (
    updatedPerson.organizationId &&
    updatedPerson.organizationId !== selectedOrganizationId.value
  ) {
    const org = await organizationStore.get(updatedPerson.organizationId)
    if (org && org.id) {
      selectedOrganization.value = org
      selectedOrganizationId.value = org.id
      onOrganizationSelect({ value: org })
    }
  }
  toast.add({
    severity: 'success',
    summary: 'Success',
    detail: 'Contact updated successfully',
    life: 3000,
  })
}

function onOrganizationUpdated(updatedOrg: Organization) {
  if (!updatedOrg.id) {
    console.error('Updated organization has no id')
    return
  }
  organizationStore.update(updatedOrg.id, updatedOrg)
  if (selectedOrganizationId.value === updatedOrg.id && transaction.value?.id) {
    transactionStore.updateDraft({
      organizationSnapshot: {
        name: updatedOrg.name || '',
        phone: updatedOrg.phone || '',
        email: updatedOrg.email || '',
        streetAddress: updatedOrg.address || '',
        city: updatedOrg.city || '',
        state: updatedOrg.state || '',
        postalCode: updatedOrg.postalCode || '',
        country: updatedOrg.country || '',
      },
    })
  }
  toast.add({
    severity: 'success',
    summary: 'Success',
    detail: 'Company updated successfully',
    life: 3000,
  })
  showEditOrganizationDialog.value = false
}

async function openEditOrganizationDialog() {
  const org = await organizationStore.get(selectedOrganizationId.value)
  if (org) {
    editingOrganization.value = { ...org }
    showEditOrganizationDialog.value = true
  } else {
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'Could not find company details',
      life: 3000,
    })
  }
}

function clearSelectedOrganization() {
  selectedOrganizationId.value = ''
  selectedOrganization.value = null
  if (transaction.value?.id) {
    transactionStore.updateDraft({
      organizationId: '',
      organizationSnapshot: undefined,
    })
  }
  toast.add({
    severity: 'info',
    summary: 'Cleared',
    detail: 'Company selection cleared',
    life: 3000,
  })
}

async function cancel() {
  if (transactionStore.hasUnsavedChanges) {
    confirm.require({
      message: 'You have unsaved changes. Are you sure you want to discard them?',
      header: 'Discard Changes',
      icon: 'pi pi-exclamation-triangle',
      acceptClass: 'p-button-danger',
      accept: async () => {
        await transactionStore.cancelEditing()
        router.push('/transaction')
      },
    })
  } else {
    await transactionStore.cancelEditing()
    router.push('/transaction')
  }
}

async function scrub() {
  if (!selectedPersonId.value) {
    toast.add({
      severity: 'warn',
      summary: 'No Person Selected',
      detail: 'Please select a person to scrub',
      life: 3000,
    })
    return
  }

  try {
    // Permanently delete the current ID document if it exists
    if (currentIdDocument.value?.id) {
      await firestoreService.delete('id_documents', currentIdDocument.value.id)
    }

    // Permanently delete the person
    await firestoreService.delete('people', selectedPersonId.value)

    // Reset all related state
    selectedPersonId.value = ''
    selectedPerson.value = null
    currentIdDocument.value = null
    showIdScanner.value = false
    showIdCaptureDialog.value = false
    showPersonForm.value = false
    showManualSearch.value = false
    showFullSearch.value = false

    toast.add({
      severity: 'success',
      summary: 'Person Deleted',
      detail: 'Person and ID document have been permanently deleted',
      life: 3000,
    })
  } catch (error) {
    console.error('Failed to scrub person:', error)
    toast.add({
      severity: 'error',
      summary: 'Delete Failed',
      detail: 'Failed to delete person and ID document',
      life: 3000,
    })
  }
}

function addNewItem() {
  transactionStore.initNewItem()
  editingItemIndex.value = -1
  showItemDialog.value = true
}

function editItem(index: number) {
  transactionStore.editItem(index)
  editingItemIndex.value = index
  showItemDialog.value = true
}

function removeItem(index: number) {
  transactionStore.removeItem(index)
}

function onItemSaved() {
  if (transactionModel.value) {
    transactionStore.updateDraft({ total_amount: transactionModel.value.totalAmountNumber })
  }
  showItemDialog.value = false
}

function onItemCancelled() {
  showItemDialog.value = false
}

function savePartySignature(signatureData: { dataUrl: string }) {
  if (transaction.value?.id) {
    transactionStore.updateDraft({
      party_signature: {
        data_url: signatureData.dataUrl,
        timestamp: new Date(),
      },
    })
  }
}

async function submitWizard() {
  try {
    const errors = gatherValidationErrors()

    const submit = async () => {
      if (transaction.value?.id) {
        await transactionStore.updateDraft({ transaction_status: 'submitted' })
        await transactionStore.saveChanges()
      }
      toast.add({
        severity: 'success',
        summary: 'Submitted',
        detail: 'Purchase saved and marked as submitted',
        life: 3000,
      })
      router.push(`/transaction/${transaction.value.id}`)
    }

    if (errors.length > 0) {
      const message = `We found the following issues:\n\n- ${errors.join('\n- ')}\n\nAre you sure you want to submit this ticket?`
      confirm.require({
        message,
        header: 'Submit with Warnings?',
        icon: 'pi pi-exclamation-triangle',
        acceptLabel: 'Submit Anyway',
        rejectLabel: 'Cancel',
        accept: async () => {
          await submit()
        },
      })
      return
    }

    await submit()
  } catch (e: any) {
    console.error('Error finalizing transaction:', e)
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: e?.message || 'Failed to submit',
      life: 5000,
    })
  }
}

function gatherValidationErrors(): string[] {
  const errors: string[] = []

  const hasSelection = !!(selectedPersonId.value || selectedOrganizationId.value)
  if (!hasSelection) {
    errors.push('Select a person or company')
  }

  if (selectedPersonId.value && !satisfyPersonRules()) {
    errors.push(...getPersonValidationErrors())
  }

  if (!step2Complete.value) {
    errors.push(...getVehicleValidationErrors())
  }

  if (!step3Complete.value) {
    if (itemsValidation.value.invalidIndexes.length > 0) {
      itemsValidation.value.invalidIndexes.forEach((idx) => {
        const itemErrors = itemsValidation.value.errorsByIndex[idx] || []
        if (itemErrors.length > 0) {
          errors.push(`Item ${idx + 1}: ${itemErrors.join(', ')}`)
        }
      })
    } else {
      errors.push('One or more items have missing information')
    }
  }

  if (!step4Complete.value) {
    errors.push('Supplier signature is missing')
  }

  return errors
}

// Scanner handlers
function openIdScanner() {
  showIdScanner.value = true
}

// IDDocument event from scanner (created automatically when personId is known or existing document found)
async function onIdDocumentSelect(doc: IDDocument) {
  try {
    console.log('[PurchaseWizard] onIdDocumentSelect called with doc:', {
      id: doc.id,
      personId: doc.personId,
      frontImageUrl: doc.frontImageUrl,
      barcodeHash: doc.barcodeHash,
    })

    // Select person based on ID Document
    if (doc.personId) {
      const person = await personStore.get(doc.personId)
      console.log(
        '[PurchaseWizard] Found person:',
        person ? { id: person.id, name: `${person.firstName} ${person.lastName}` } : 'NOT FOUND',
      )
      if (person) {
        console.log('[PurchaseWizard] Calling onPersonSelect with person:', person.id)
        await onPersonSelect({ value: person })
      }
    }

    // Update transaction and person with photo URL
    if (doc.frontImageUrl && transaction.value?.id) {
      await transactionStore.updateDraft({
        personSnapshot: {
          ...transaction.value.personSnapshot,
          photoIdUrl: doc.frontImageUrl,
        },
      })
    }
    if (selectedPerson.value?.id && doc.frontImageUrl) {
      await personStore.update(selectedPerson.value.id, { photoIdUrl: doc.frontImageUrl })
      selectedPerson.value.photoIdUrl = doc.frontImageUrl
    }
  } catch (e) {
    console.error('Failed to process created IDDocument from scan', e)
  }
}

// New Person form methods
function startCreatePerson() {
  showManualSearch.value = false
  showPersonForm.value = true
  isEditingPerson.value = false
  personForm.value = {
    firstName: searchFirstName.value || '',
    lastName: searchLastName.value || '',
    middleName: '',
    idNumber: '',
    email: '',
    phone: '',
    streetAddress: '',
    city: '',
    state: '',
    postalCode: '',
    country: '',
    type: 'supplier' as any,
    status: 'supplier',
  }
  personFormSubmitted.value = false
}

function cancelPersonForm() {
  showPersonForm.value = false
  showManualSearch.value = false
  showFullSearch.value = false
  personFormSubmitted.value = false
}

async function savePersonForm() {
  personFormSubmitted.value = true

  // Validate required fields
  if (!personForm.value.firstName || !personForm.value.lastName) {
    toast.add({
      severity: 'error',
      summary: 'Validation Error',
      detail: 'Please fill in all required fields',
      life: 3000,
    })
    return
  }

  savingPerson.value = true

  try {
    if (isEditingPerson.value) {
      // Update existing person
      const updatedPerson = { ...personForm.value }
      await onPersonUpdated(updatedPerson as Person)
    } else {
      // Create new person
      const newPersonData = { ...personForm.value }
      await onPersonCreated(newPersonData as Person)
    }

    showPersonForm.value = false
    personFormSubmitted.value = false
  } catch (error) {
    console.error('Error saving person:', error)
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'Failed to save person',
      life: 3000,
    })
  } finally {
    savingPerson.value = false
  }
}

function editSelectedPerson() {
  if (selectedPerson.value) {
    showPersonForm.value = true
    isEditingPerson.value = true
    personForm.value = { ...selectedPerson.value }
    personFormSubmitted.value = false
  }
}

function clearSelectedPerson() {
  selectedPersonId.value = ''
  selectedPerson.value = null
  showPersonForm.value = false
  showManualSearch.value = false
  if (transaction.value?.id) {
    transactionStore.updateDraft({
      personId: '',
      personSnapshot: undefined,
    })
  }
}

// Inline phone editing methods
function startEditingPhone() {
  isEditingPhone.value = true
  inlinePhoneValue.value = selectedPerson.value?.phone || ''
}

function cancelEditingPhone() {
  isEditingPhone.value = false
  inlinePhoneValue.value = ''
}

async function savePhoneNumber() {
  if (!selectedPerson.value || !selectedPerson.value.id || !inlinePhoneValue.value.trim()) {
    return
  }

  savingPhone.value = true
  try {
    await personStore.update(selectedPerson.value.id, {
      phone: inlinePhoneValue.value.trim(),
    })

    // Get the updated person
    const updatedPerson = await personStore.get(selectedPerson.value.id)
    if (!updatedPerson) {
      throw new Error('Person not found after update')
    }

    // Update the selected person reference
    selectedPerson.value = {
      ...selectedPerson.value,
      phone: updatedPerson.phone,
    }

    // Update transaction snapshot if it exists
    if (transaction.value?.id) {
      transactionStore.updateDraft({
        personSnapshot: {
          ...transaction.value.personSnapshot,
          phone: updatedPerson.phone,
        },
      })
    }

    isEditingPhone.value = false
    inlinePhoneValue.value = ''

    toast.add({
      severity: 'success',
      summary: 'Phone number updated',
      detail: 'The phone number has been saved successfully.',
      life: 3000,
    })
  } catch (error) {
    console.error('Failed to update phone number:', error)
    toast.add({
      severity: 'error',
      summary: 'Update failed',
      detail: 'Failed to save the phone number. Please try again.',
      life: 3000,
    })
  } finally {
    savingPhone.value = false
  }
}

async function performManualSearch() {
  if (!searchFirstName.value && !searchLastName.value) {
    return
  }

  manualSearchPerformed.value = true
  showAllSearchResults.value = false // Reset to show only active by default
  showFullSearch.value = true // Switch to full search mode when searching

  try {
    // Use person service for search
    const search = await personStore.search()
    const searchResults = await search

      .byName(searchFirstName.value || undefined, searchLastName.value || undefined)
      .run()

    // Sort results: active people first, then by last transaction date, then by name
    manualSearchResults.value = searchResults.sort((a, b) => {
      // First priority: active status
      const aIsActive = a.status === 'supplier'
      const bIsActive = b.status === 'supplier'
      if (aIsActive && !bIsActive) return -1
      if (!aIsActive && bIsActive) return 1

      // Second priority: last transaction date (most recent first)
      const aLastTx = a.lastTransactionDate ? new Date(a.lastTransactionDate).getTime() : 0
      const bLastTx = b.lastTransactionDate ? new Date(b.lastTransactionDate).getTime() : 0
      if (aLastTx !== bLastTx) return bLastTx - aLastTx

      // Third priority: alphabetical by last name, then first name
      const aLastName = a.lastName?.toLowerCase() || ''
      const bLastName = b.lastName?.toLowerCase() || ''
      if (aLastName !== bLastName) return aLastName.localeCompare(bLastName)

      const aFirstName = a.firstName?.toLowerCase() || ''
      const bFirstName = b.firstName?.toLowerCase() || ''
      return aFirstName.localeCompare(bFirstName)
    })
  } catch (error) {
    console.error('Error searching people:', error)
    manualSearchResults.value = []
  }
}

// Computed properties for filtered search results
const activeSearchResults = computed(() =>
  manualSearchResults.value.filter((person) => person.status === 'supplier'),
)

const nonActiveSearchResults = computed(() =>
  manualSearchResults.value.filter((person) => person.status !== 'supplier'),
)

const displayedSearchResults = computed(() =>
  showAllSearchResults.value ? manualSearchResults.value : activeSearchResults.value,
)

// Helper function to get status severity for badges
function getStatusSeverity(status?: string) {
  switch (status) {
    case 'supplier':
      return 'success'
    case 'inactive':
      return 'danger'
    case 'lead':
      return 'info'
    default:
      return 'secondary'
  }
}

// Helper function to get transaction count for a person
function getTransactionCount(personId: string) {
  if (!transactionStore.transactions || transactionStore.loading) return 0
  return transactionStore.transactions.filter((tx: any) => tx.personId === personId).length
}

// Function to toggle showing all search results
function toggleShowAllResults() {
  showAllSearchResults.value = !showAllSearchResults.value
}

// Function to start full search mode
function startFullSearch() {
  showManualSearch.value = true
  showFullSearch.value = true
}

// Function to close search completely
function closeSearch() {
  showManualSearch.value = false
  showFullSearch.value = false
  manualSearchPerformed.value = false
  manualSearchResults.value = []
  searchFirstName.value = ''
  searchLastName.value = ''
  showAllSearchResults.value = false
}

// Function to close full search mode (kept for backward compatibility)
function closeFullSearch() {
  showFullSearch.value = false
  manualSearchPerformed.value = false
  manualSearchResults.value = []
  searchFirstName.value = ''
  searchLastName.value = ''
  showAllSearchResults.value = false
}

// File input ref
const fileInputRef = ref()
const showFileUpload = ref(false)

// Attachment handling methods
function toggleFileUpload() {
  showFileUpload.value = !showFileUpload.value
}
function triggerFileSelect() {
  if (fileInputRef.value) {
    fileInputRef.value.click()
  }
}

async function onFileSelect(event: Event) {
  const target = event.target as HTMLInputElement
  const files = target.files
  if (!files || files.length === 0 || !selectedPerson.value) return

  // Process each selected file
  for (const file of files) {
    await processFileUpload(file)
  }

  // Clear the input so the same file can be selected again
  target.value = ''
}

async function processFileUpload(file: File) {
  if (!selectedPerson.value) return

  try {
    // TODO: Implement attachment functionality
    console.log('Attachment functionality not yet implemented in V2 architecture')

    // For now, just show success message
    toast.add({
      severity: 'success',
      summary: 'Document Added',
      detail: 'Document has been uploaded successfully (attachment functionality pending)',
      life: 3000,
    })
  } catch (error) {
    console.error('Error uploading attachment:', error)
    toast.add({
      severity: 'error',
      summary: 'Upload Failed',
      detail: 'Failed to upload document. Please try again.',
      life: 3000,
    })
  }
}

function getFileIcon(fileType: string) {
  if (fileType.startsWith('image/')) return 'pi pi-image text-blue-500'
  if (fileType === 'application/pdf') return 'pi pi-file-pdf text-red-500'
  return 'pi pi-file text-gray-500'
}

function viewAttachment(attachment: { url: string }) {
  window.open(attachment.url, '_blank')
}

function removeAttachment(attachment: { id: string }) {
  if (!selectedPerson.value) return

  confirm.require({
    message: 'Are you sure you want to remove this document?',
    header: 'Confirm Removal',
    icon: 'pi pi-exclamation-triangle',
    accept: async () => {
      try {
        // TODO: Implement attachment removal functionality
        console.log('Attachment removal functionality not yet implemented in V2 architecture')

        // Remove from local state
        if (selectedPerson.value?.attachments) {
          selectedPerson.value.attachments = selectedPerson.value.attachments.filter(
            (a: any) => a.id !== attachment.id,
          )
        }

        toast.add({
          severity: 'success',
          summary: 'Document Removed',
          detail: 'Document has been removed successfully',
          life: 3000,
        })
      } catch (error) {
        console.error('Error removing attachment:', error)
        toast.add({
          severity: 'error',
          summary: 'Removal Failed',
          detail: 'Failed to remove document. Please try again.',
          life: 3000,
        })
      }
    },
  })
}

// Handle new person from scanner
async function onPersonNew(person: Person) {
  if (!person.id) {
    console.error('New person has no id')
    return
  }
  console.log('[PurchaseWizard] New person created from scanner:', person.id)

  // Set the questionnaire person ID for later use
  questionnairePersonId.value = person.id
  console.log('[PurchaseWizard] Set questionnaire person ID:', questionnairePersonId.value)

  // Also handle the person selection normally
  await onPersonSelect({ value: person })

  // The questionnaire step will automatically show since the person status is not "active"
  console.log(
    '[PurchaseWizard] Person status:',
    person.status,
    'Questionnaire will show:',
    showSupplierQuestionnaireStep.value,
  )
}

// Handle supplier questionnaire saved
async function saveQuestionnaire() {
  try {
    console.log('[PurchaseWizard] Supplier questionnaire saved:', questionnaireData.value)

    // Mark the person as "active" since they've completed the questionnaire
    if (selectedPerson.value && typeof selectedPerson.value.id === 'string') {
      const updatedPerson = { ...selectedPerson.value, status: 'supplier' as const }
      await personStore.update(selectedPerson.value.id, updatedPerson)
      selectedPerson.value = updatedPerson
    }

    // Here you could save the questionnaire data to a separate collection
    // or add it as metadata to the person record
    // For now, we'll just log it and show a success message

    toast.add({
      severity: 'success',
      summary: 'Thank You!',
      detail: `Your feedback has been recorded.${companyName.value ? ` Welcome to ${companyName.value}!` : ''}`,
      life: 3000,
    })

    // Clear the questionnaire person ID
    questionnairePersonId.value = ''
  } catch (error) {
    console.error('Failed to save supplier questionnaire:', error)
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'Failed to save questionnaire data',
      life: 3000,
    })
  }
}

// Handle supplier questionnaire skipped
async function skipQuestionnaire() {
  console.log('[PurchaseWizard] Supplier questionnaire skipped')

  // Mark the person as "active" even if they skipped the questionnaire
  if (selectedPerson.value && selectedPerson.value.id) {
    const updatedPerson = { ...selectedPerson.value, status: 'supplier' as const }
    await personStore.update(selectedPerson.value.id, updatedPerson)
    selectedPerson.value = updatedPerson
  }

  // Clear the questionnaire person ID
  questionnairePersonId.value = ''
}

// Handle ID capture cancelled
function onIdCaptureCancelled() {
  showIdCaptureDialog.value = false
}

// Handle ID document upload
async function onIdDocumentSaved(frontImageUrl: string, backImageUrl?: string) {
  try {
    if (!selectedPersonId.value) return

    // Use ID document service for handling
    if (currentIdDocument.value?.id) {
      await idDocumentService.update({
        ...currentIdDocument.value,
        frontImageUrl: frontImageUrl,
        backImageUrl: backImageUrl || undefined,
      })
    } else {
      // Create new document only if no current document exists
      const doc = IDDocumentModel.create({
        personId: selectedPersonId.value,
        type: 'driver_license',
        frontImageUrl: frontImageUrl,
        backImageUrl: backImageUrl,
      })
      const createdDoc = await idDocumentService.add(doc)

      // Update person with currentIdDocumentId
      await personStore.update(selectedPersonId.value, {
        currentIdDocumentId: createdDoc.id,
        updatedAt: new Date(),
      })
    }

    // Back-compat: also update the transaction snapshot/person record fields used today
    if (transaction.value?.id) {
      await transactionStore.updateDraft({
        personSnapshot: {
          ...transaction.value.personSnapshot,
          photoIdUrl: frontImageUrl,
        },
      })
    }
    if (selectedPerson.value?.id) {
      await personStore.update(selectedPerson.value.id, { photoIdUrl: frontImageUrl })
      selectedPerson.value.photoIdUrl = frontImageUrl
    }

    toast.add({
      severity: 'success',
      summary: 'Success',
      detail: 'ID document uploaded successfully',
      life: 3000,
    })
  } catch (error) {
    console.error('Error saving ID document:', error)
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'Failed to save ID document',
      life: 3000,
    })
  }
}

// Keep new org/person types aligned when transaction type changes (wizard is purchase-focused)
watch(
  () => transaction.value?.transaction_type,
  (newType) => {
    const type = newType === 'purchase' ? 'supplier' : 'customer'
    newPerson.value.type = type as any
    newOrganization.value.type = type
  },
)

// Load data on mount
onMounted(async () => {
  // Prevent multiple simultaneous initializations
  if (isInitializing.value) {
    console.log('Already initializing, skipping...')
    return
  }

  isInitializing.value = true

  try {
    console.log('Starting PurchaseWizardView initialization...')

    // Load transaction first
    await transactionStore.startEditing(transactionId.value)
    if (!transactionStore.draft) {
      toast.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Could not load transaction for editing',
        life: 3000,
      })
      router.push('/transaction')
      return
    }

    // Load commodity types using service
    const initData = await initializationService.initializePurchaseForm(transactionId.value)
    commodityTypes.value = initData.commodityTypes

    // Load stores sequentially to avoid race conditions and multiple loads
    console.log('Loading stores sequentially to prevent multiple loads...')

    // V2 stores don't need explicit loading - they load data on demand
    console.log('V2 stores load data on demand, no explicit loading needed')

    // Load transactions (old store method)
    console.log('Loading transactions...')
    await transactionStore.loadTransactions()

    if (transactionStore.draft.personId) {
      const personId = transactionStore.draft.personId
      if (!personId) {
        return
      }
      selectedPersonId.value = personId
      const person = await personStore.get(personId)
      if (person) {
        selectedPerson.value = {
          ...person,
          displayName: `${person.firstName}${person.middleName ? ' ' + person.middleName : ''} ${person.lastName}`,
        }
      }
    }
    if (transactionStore.draft.organizationId) {
      selectedOrganizationId.value = transactionStore.draft.organizationId
      const org = await organizationStore.get(selectedOrganizationId.value)
      if (org) {
        selectedOrganization.value = org
      }
    }
    if (transactionStore.draft.vehicleId) {
      selectedVehicleId.value = transactionStore.draft.vehicleId as string
      const v = await vehicleStore.getVehicleById(selectedVehicleId.value)
      if (v) {
        selectedVehicle.value = v
        vehicleTagSearch.value = v.tagNumber || ''
      }
    } else if (transactionStore.draft.vehicleSnapshot) {
      // Use snapshot details for display if available
      const vs = transactionStore.draft.vehicleSnapshot as Partial<Vehicle>
      selectedVehicle.value = {
        id: undefined,
        tagNumber: (vs.tagNumber as string) || '',
        state: (vs.state as string) || '',
        year: vs.year as number | undefined,
        make: vs.make as string | undefined,
        model: vs.model as string | undefined,
        color: vs.color as string | undefined,
        vin: vs.vin as string | undefined,
        notes: vs.notes as string | undefined,
        createdAt: new Date(),
        updatedAt: new Date(),
      }
      vehicleTagSearch.value = selectedVehicle.value.tagNumber || ''
    }
  } catch (error: any) {
    console.error('Error loading data:', error)
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'Failed to load necessary data: ' + (error?.message || ''),
      life: 5000,
    })
  } finally {
    isInitializing.value = false
    console.log('PurchaseWizardView initialization completed')
  }
})
</script>

<style scoped>
.signature-preview {
  border: 1px solid #ddd;
  border-radius: 4px;
  height: 120px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  width: 100%;
}
.signature-image {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 10px;
}
.signature-image img {
  max-width: 100%;
  max-height: 80%;
  object-fit: contain;
  width: auto;
  height: auto;
}
.signature-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  color: #aaa;
}
.signature-placeholder i {
  font-size: 2rem;
  margin-bottom: 0.5rem;
}

.p-card-caption {
  border-bottom: 1px solid #770606;
}

/* Override PrimeVue stepper background using CSS custom property */
.p-stepper {
  --p-stepper-steppanel-background: transparent;
}

/* Panel visual separation styles */
.person-panel .p-panel-header {
  border-bottom: 2px solid #e5e7eb;
  background-color: #f9fafb;
  color: #374151;
  font-weight: 600;
}

.p-panel-header span {
  font-size: 1.5rem;
}

/* Panel styling for company */
.company-panel .p-panel-header {
  border-bottom: 2px solid #e5e7eb;
  background-color: #f9fafb;
  color: #374151;
  font-weight: 600;
}

.company-panel .p-panel-header:hover {
  background-color: #f3f4f6;
}
</style>
